import React, { useState, useEffect, useRef } from 'react';
import { X, User, Calendar, MapPin, Briefcase, Phone, Camera } from 'lucide-react';
import type { FamilyMember } from '../../../types/family';
import { useAuthStore } from '../../../store/authStore';

interface MemberEditModalProps {
  member: FamilyMember;
  isOpen: boolean;
  onClose: () => void;
  onSave: (member: FamilyMember) => void;
}

export const MemberEditModal: React.FC<MemberEditModalProps> = ({
  member,
  isOpen,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<FamilyMember>(member);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData(member);
      setErrors({});
    }
  }, [member, isOpen]);

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, photoUrl: 'Please select an image file' }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, photoUrl: 'Image must be under 5MB' }));
      return;
    }

    // Upload to R2 via the worker, then store the returned URL in D1.
    setUploadingPhoto(true);
    setErrors(prev => ({ ...prev, photoUrl: undefined }));
    try {
      const token = useAuthStore.getState().token;
      const apiBase = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '');
      const formData = new FormData();
      formData.append('photo', file);
      const res = await fetch(`${apiBase}/members/${member.id}/photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Upload failed');
      }
      const data = await res.json();
      setFormData(prev => ({ ...prev, photoUrl: data.photoUrl }));
    } catch (err) {
      setErrors(prev => ({ ...prev, photoUrl: err instanceof Error ? err.message : 'Upload failed' }));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleInputChange = (field: keyof FamilyMember, value: string | boolean | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <User className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Edit Member Profile
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Profile Photo */}
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                {formData.photoUrl ? (
                  <img src={formData.photoUrl} alt={formData.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                aria-label="Change profile photo"
              >
                <Camera className="w-4 h-4" />
              </button>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Profile Photo</p>
              <p className="text-xs text-gray-500 mb-2">JPG, PNG or GIF. Max 5MB.</p>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50"
                >
                  {uploadingPhoto ? 'Uploading…' : 'Upload Photo'}
                </button>
                {formData.photoUrl && !uploadingPhoto && (
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, photoUrl: undefined }))}
                    className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
              />
              {errors.photoUrl && (
                <p className="mt-1 text-sm text-red-600">{errors.photoUrl}</p>
              )}
            </div>
          </div>

          {/* Basic Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2 text-blue-600" />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Full Name */}
            <div>
              <label htmlFor="member-name" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                id="member-name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter full name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Nickname */}
            <div>
              <label htmlFor="member-nickname" className="block text-sm font-medium text-gray-700 mb-1">
                Nickname
              </label>
              <input
                id="member-nickname"
                type="text"
                value={formData.nickname ?? ''}
                onChange={(e) => handleInputChange('nickname', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                placeholder="Daily nickname"
              />
            </div>

            {/* Gender */}
            <div>
              <label htmlFor="member-gender" className="block text-sm font-medium text-gray-700 mb-1">
                Gender *
              </label>
              <select
                id="member-gender"
                value={formData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500 ${
                  errors.gender ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="unrecorded">Not recorded</option>
              </select>
              {errors.gender && (
                <p className="mt-1 text-sm text-red-600">{errors.gender}</p>
              )}
            </div>

            {/* Generation */}
            <div>
              <label htmlFor="member-generation" className="block text-sm font-medium text-gray-700 mb-1">
                Generation
              </label>
              <input
                id="member-generation"
                type="number"
                min="1"
                max="10"
                value={formData.generation}
                onChange={(e) => handleInputChange('generation', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Husband */}
            <div>
              <label htmlFor="member-husband" className="block text-sm font-medium text-gray-700 mb-1">
                Husband
              </label>
              <input
                id="member-husband"
                type="text"
                value={formData.husband ?? ''}
                onChange={(e) => handleInputChange('husband', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                placeholder="Husband's name"
              />
            </div>
          </div>
          </div>

          {/* Birth Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-blue-600" />
              Birth Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="member-birth-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Birth Date
                </label>
                <input
                  id="member-birth-date"
                  type="date"
                  value={formData.birthDate}
                  onChange={(e) => handleInputChange('birthDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500 ${
                    errors.birthDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.birthDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.birthDate}</p>
                )}
              </div>

              <div>
                <label htmlFor="member-birth-place" className="block text-sm font-medium text-gray-700 mb-1">
                  Birth Place
                </label>
                <input
                  id="member-birth-place"
                  type="text"
                  value={formData.birthPlace ?? ''}
                  onChange={(e) => handleInputChange('birthPlace', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500 ${
                    errors.birthPlace ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="City/place of birth"
                />
                {errors.birthPlace && (
                  <p className="mt-1 text-sm text-red-600">{errors.birthPlace}</p>
                )}
              </div>
            </div>
          </div>

          {/* Life Status */}
          <div className="border-t pt-6">
            <div className="flex items-center space-x-4 mb-4">
              <div className="flex items-center">
                <input
                  id="member-alive"
                  type="checkbox"
                  checked={formData.isAlive}
                  onChange={(e) => handleInputChange('isAlive', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="member-alive" className="ml-2 text-sm font-medium text-gray-700">
                  Living
                </label>
              </div>
            </div>

            {!formData.isAlive && (
              <div>
                <label htmlFor="member-death-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Death Date
                </label>
                <input
                  id="member-death-date"
                  type="date"
                  value={formData.deathDate ?? ''}
                  onChange={(e) => handleInputChange('deathDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500 max-w-xs"
                />
              </div>
            )}
          </div>

          {/* Professional Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Briefcase className="w-5 h-5 mr-2 text-blue-600" />
              Professional Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="member-profession" className="block text-sm font-medium text-gray-700 mb-1">
                  Profession/Occupation
                </label>
                <input
                  id="member-profession"
                  type="text"
                  value={formData.profession ?? ''}
                  onChange={(e) => handleInputChange('profession', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  placeholder="Occupation or profession"
                />
              </div>

              <div>
                <label htmlFor="member-education" className="block text-sm font-medium text-gray-700 mb-1">
                  Education
                </label>
                <input
                  id="member-education"
                  type="text"
                  value={formData.education ?? ''}
                  onChange={(e) => handleInputChange('education', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  placeholder="Latest education"
                />
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-blue-600" />
              Location Information
            </h3>
            <div>
              <label htmlFor="member-location" className="block text-sm font-medium text-gray-700 mb-1">
                Current Location
              </label>
              <input
                id="member-location"
                type="text"
                value={formData.currentLocation ?? ''}
                onChange={(e) => handleInputChange('currentLocation', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                placeholder="Current city/area of residence"
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Phone className="w-5 h-5 mr-2 text-blue-600" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="member-phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  id="member-phone"
                  type="tel"
                  value={formData.phone ?? ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  placeholder="+62812345678"
                />
              </div>

              <div>
                <label htmlFor="member-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="member-email"
                  type="email"
                  value={formData.email ?? ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  placeholder="name@email.com"
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="member-facebook" className="block text-sm font-medium text-gray-700 mb-1">
                Facebook Profile URL
              </label>
              <input
                id="member-facebook"
                type="url"
                value={formData.facebookUrl ?? ''}
                onChange={(e) => handleInputChange('facebookUrl', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                placeholder="https://facebook.com/username"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="border-t pt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};